import React, { useContext } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { ArticuloItem } from "../../../services/api/articulosApi";
import { DarkModeContext } from "../../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", hover: "rgba(255,255,255,0.04)" },
};

interface SectionLabelProps { children: React.ReactNode; p: typeof P.light }
const SectionLabel: React.FC<SectionLabelProps> = ({ children, p }) => (
  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: p.textT, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5, pb: 0.75, borderBottom: `1px solid ${p.border}` }}>
    {children}
  </Typography>
);

interface FieldProps { label: string; value: React.ReactNode; p: typeof P.light; mono?: boolean }
const Field: React.FC<FieldProps> = ({ label, value, p, mono }) => (
  <Box>
    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>{label}</Typography>
    <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: p.text, fontFamily: mono ? "'IBM Plex Mono', monospace" : "inherit" }}>
      {value}
    </Typography>
  </Box>
);

interface PriceCardProps { label: string; value: string; icon: React.ReactNode; color: string; p: typeof P.light }
const PriceCard: React.FC<PriceCardProps> = ({ label, value, icon, color, p }) => (
  <Box sx={{ p: 2, borderRadius: "10px", border: `1px solid ${color}30`, bgcolor: `${color}0A`, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box sx={{ color, opacity: 0.8, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
      <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{value}</Typography>
    </Box>
  </Box>
);

interface ArticuloDetailsModalProps {
  open: boolean;
  onClose: () => void;
  articulo: ArticuloItem | null;
}

const ArticuloDetailsModal: React.FC<ArticuloDetailsModalProps> = ({ open, onClose, articulo }) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  if (!articulo) return null;

  const { Nombre, Descripcion, FechaAlta, EAN, UnidadesPorEmbalaje, Referencia, PrecioCompra, PrecioVenta, Descuento, Activo, categoria, embalaje, impuesto } = articulo;

  const margen = PrecioCompra > 0 ? Math.round(((PrecioVenta - PrecioCompra) / PrecioCompra) * 100) : null;
  const margenColor = margen === null ? p.textT : margen < 0 ? "#ef5350" : margen < 15 ? "#ff9800" : "#4caf50";

  const precioFmt = (v: number) => `$ ${v.toLocaleString("es-CO", { minimumFractionDigits: 0 })}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" } }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}`, bgcolor: p.bgS }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}>
              <InventoryIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>{Nombre}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                <Typography sx={{ fontSize: "0.75rem", fontFamily: "'IBM Plex Mono', monospace", color: p.textT }}>{EAN}</Typography>
                <Chip label={Activo ? "Activo" : "Inactivo"} size="small" sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, bgcolor: Activo ? "#e8f5e9" : "#ffebee", color: Activo ? "#2e7d32" : "#c62828" }} />
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: p.bg }}>
          <Grid container spacing={3}>
            {/* Información general */}
            <Grid item xs={12}>
              <SectionLabel p={p}>Información General</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Field label="Descripción" value={Descripcion || "Sin descripción"} p={p} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Field label="Referencia" value={Referencia || "—"} p={p} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Field label="Fecha de Alta" value={new Date(FechaAlta).toLocaleDateString("es-CO")} p={p} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider sx={{ borderColor: p.border }} /></Grid>

            {/* Categorización */}
            <Grid item xs={12}>
              <SectionLabel p={p}>Categorización</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bgS, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CategoryIcon sx={{ color: p.accent, fontSize: 22 }} />
                    <Box>
                      <Typography sx={{ fontSize: "0.7rem", color: p.textT, fontWeight: 600, textTransform: "uppercase" }}>Categoría</Typography>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>{categoria?.Nombre || "—"}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bgS, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <InventoryIcon sx={{ color: p.accent, fontSize: 22 }} />
                    <Box>
                      <Typography sx={{ fontSize: "0.7rem", color: p.textT, fontWeight: 600, textTransform: "uppercase" }}>Embalaje</Typography>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>{embalaje?.Nombre || "—"}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bgS, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PercentIcon sx={{ color: p.accent, fontSize: 22 }} />
                    <Box>
                      <Typography sx={{ fontSize: "0.7rem", color: p.textT, fontWeight: 600, textTransform: "uppercase" }}>Impuesto</Typography>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>{impuesto?.Nombre || "—"}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Unidades por embalaje" value={`${UnidadesPorEmbalaje} unidades`} p={p} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider sx={{ borderColor: p.border }} /></Grid>

            {/* Precios */}
            <Grid item xs={12}>
              <SectionLabel p={p}>Precios y Márgenes</SectionLabel>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}>
                  <PriceCard label="Precio Compra" value={precioFmt(PrecioCompra)} icon={<AttachMoneyIcon />} color="#3498db" p={p} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <PriceCard label="Precio Venta" value={precioFmt(PrecioVenta)} icon={<AttachMoneyIcon />} color="#4caf50" p={p} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <PriceCard label="Descuento" value={`${Descuento}%`} icon={<PercentIcon />} color="#f39c12" p={p} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ p: 2, borderRadius: "10px", border: `1px solid ${margenColor}30`, bgcolor: `${margenColor}0A`, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ color: margenColor, opacity: 0.8, display: "flex" }}><TrendingUpIcon /></Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>Margen Bruto</Typography>
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: margenColor, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {margen !== null ? `${margen}%` : "—"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
  );
};

export default ArticuloDetailsModal;
